import { useEffect, useRef } from "react"
import { store } from "aldoo-ra/Store"
import { getValue } from "aldoo-ra/Value"
import { ListProducts, LIST_FIELDS, WinbackRestore } from "../api"
import { getDeviceID } from "../storeApi"
import { toCartItem, lineKey } from "./cartItems"
import { scheduleStockCheck } from "../store/stockCheck"
import useSiteLanguage from "../hooks/useSiteLanguage"
import { useCurrency } from "../store/currency"

// A link that fills the cart — what an email sends a buyer back with:
//   /bg/?add=<pid>,<pid>*2,…            those products in the cart (×n), the cart opened
//   /bg/?add=…&replace=1                the cart is exactly them (what else was in it goes)
// Each product ends at the quantity the link names, never on top of what the cart
// already holds, so opening the link twice (or with the cart still there) doubles
// nothing. Products the store cannot sell now (gone, not found) are left out.
//   /bg/?winback=<token>                a win-back letter's link: the cart the letter is about, open again
//                                       on this device and its products put back (a suggestions letter's
//                                       link only counts the click)
// The parameters leave the address once read, so a reload does not do it again.
const MAX = 100

const parse = (raw) =>
  String(raw || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(0, MAX)
    .map((part) => {
      const [pid, n] = part.split("*")
      return { pid, quantity: Math.max(1, Math.min(99, parseInt(n, 10) || 1)) }
    })

export default function CartLink() {
  const language = useSiteLanguage()
  const { currency } = useCurrency()
  const done = useRef(false)

  useEffect(() => {
    if (done.current || typeof window === "undefined") return
    const url = new URL(window.location.href)
    const winback = url.searchParams.get("winback")
    if (winback) {
      done.current = true
      url.searchParams.delete("winback")
      window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash)
      ;(async () => {
        const deviceID = await Promise.resolve(getDeviceID()).catch(() => null)
        const res = await WinbackRestore(winback, deviceID).catch(() => null)
        const items = (res?.items || []).filter((item) => item?.pid)
        if (items.length) fill(items.map(({ pid, quantity }) => ({ pid, quantity: Math.max(1, Math.min(99, quantity || 1)) })), false)
      })()
      return
    }
    const wanted = parse(url.searchParams.get("add"))
    if (!wanted.length) return
    done.current = true
    const replace = url.searchParams.get("replace") === "1"
    // out of the address now: a reload opens the cart, it does not fill it again
    url.searchParams.delete("add")
    url.searchParams.delete("replace")
    url.searchParams.set("cart", "1")
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash)

    fill(wanted, replace)
  }, [language, currency])

  // the products in the cart at the quantities named, the cart opened
  async function fill(wanted, replace) {
    const res = await ListProducts({ page: 0, pids: wanted.map((w) => w.pid), items_per_page: wanted.length, language, currency, owned_only: false, fields: LIST_FIELDS }).catch(() => null)
    const byPid = new Map((res?.list || []).map((product) => [product.pid, product]))
    const found = wanted.filter((w) => byPid.has(w.pid))
    if (!found.length) return store.showCart()

    if (replace) store.emptyCart()
    // the cart as it is now (written synchronously), not a render's copy
    const inCart = new Map((getValue("aldoo-store-cart", "aldoo-store-cart") || []).map((line) => [lineKey(line), line]))
    for (const { pid, quantity } of found) {
      // a plain line's key is its pid: already there → set to the link's quantity
      if (inCart.has(pid)) store.setLine(pid, { quantity })
      else store.addToCart({ product: toCartItem(byPid.get(pid), quantity), quantity, options: {} })
    }
    scheduleStockCheck(found.map((f) => f.pid))
    store.showCart()
  }

  return null
}
